import {ChangeEvent, useRef} from 'react';
import Icon from '../Icon';
import Spinner from '../feedback/Spinner';
import {usePhotoMutations} from '../../api/posts';
import {confirm} from '../../hooks/useConfirm';
import {toast} from '../../hooks/useToast';
import {prepareUpload} from '../../lib/resizeImage';
import {uploadQueue, useUploadState} from '../../lib/uploadQueue';
import {Photo} from '../../types';

// Photos of an existing tell, on the edit screen: 76px thumbnails with remove and left/right
// ordering, in-flight uploads from the queue, and "+ add" which resizes and uploads at once.
const MAX_PHOTOS = 10;

export default function PhotoStrip({postId, photos}: {postId: string; photos: Photo[]}) {
    const {remove, reorder} = usePhotoMutations();
    const upload = useUploadState(postId);
    const input = useRef<HTMLInputElement>(null);
    const inFlight = upload?.photos.filter(p => p.status !== 'done') ?? [];
    const room = MAX_PHOTOS - photos.length - inFlight.length;

    const onFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []).slice(0, Math.max(0, room));
        event.target.value = '';
        if (files.length === 0) return;
        uploadQueue.enqueue(
            postId,
            files.map(file => ({id: crypto.randomUUID(), previewUrl: URL.createObjectURL(file), prepared: prepareUpload(file)}))
        );
    };

    const removePhoto = async (photo: Photo) => {
        if (await confirm({title: 'Remove this photo?', confirmLabel: 'Remove', destructive: true})) {
            remove.mutate({postId, photoId: photo.id}, {onSuccess: () => toast.success('Photo removed.')});
        }
    };

    const move = (photo: Photo, direction: -1 | 1) => {
        const ids = photos.map(p => p.id);
        const from = ids.indexOf(photo.id);
        const to = from + direction;
        if (to < 0 || to >= ids.length) return;
        ids.splice(from, 1);
        ids.splice(to, 0, photo.id);
        reorder.mutate({postId, photoIds: ids});
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex gap-[10px] overflow-x-auto py-1 [scrollbar-width:none]">
                {photos.map((photo, i) => (
                    <div key={photo.id} className="relative shrink-0">
                        <img src={photo.thumbUrl ?? photo.url} alt="" className="h-[76px] w-[76px] rounded-[16px] object-cover" />
                        <button type="button" aria-label="Remove photo" className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-pill bg-ink text-ground" onClick={() => void removePhoto(photo)}>
                            <Icon name="close" size={12} strokeWidth={2.5} />
                        </button>
                        {photos.length > 1 && (
                            <div className="absolute -bottom-1 left-1 right-1 flex justify-between">
                                <button type="button" aria-label="Move left" disabled={i === 0 || reorder.isPending} className="flex h-6 w-6 items-center justify-center rounded-pill bg-ink/80 text-ground disabled:opacity-30" onClick={() => move(photo, -1)}>
                                    <Icon name="back" size={12} strokeWidth={2.5} />
                                </button>
                                <button type="button" aria-label="Move right" disabled={i === photos.length - 1 || reorder.isPending} className="flex h-6 w-6 items-center justify-center rounded-pill bg-ink/80 text-ground disabled:opacity-30" onClick={() => move(photo, 1)}>
                                    <Icon name="chevron" size={12} strokeWidth={2.5} />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                {inFlight.map(p => (
                    <div key={p.id} className="relative shrink-0">
                        <img src={p.previewUrl} alt="" className="h-[76px] w-[76px] rounded-[16px] object-cover opacity-60" />
                        <span className="absolute inset-0 flex items-center justify-center">
                            {p.status === 'failed' ? (
                                <button type="button" className="rounded-pill bg-rust px-2 py-1 text-[11px] font-bold text-white" onClick={() => uploadQueue.retry(postId)}>
                                    Retry
                                </button>
                            ) : (
                                <Spinner size="sm" />
                            )}
                        </span>
                    </div>
                ))}
                {room > 0 && (
                    <button type="button" className="flex h-[76px] w-[76px] shrink-0 flex-col items-center justify-center gap-1 rounded-[16px] border-[1.5px] border-dashed border-line text-xs font-semibold text-ink-2" onClick={() => input.current?.click()}>
                        <Icon name="plus" size={20} />
                        add
                    </button>
                )}
            </div>
            {upload?.failed && <p className="problem m-0">A photo didn&rsquo;t upload: {upload.photos.find(p => p.status === 'failed')?.error}</p>}
            <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
        </div>
    );
}
